/*
 * O singura clasa de eroare pentru tot ce iese din stratul de api.
 *
 * Ecranele prind `ApiError` si arata `message` omului si `code` langa el.
 * Codurile sunt cele din tabelul §6 al contractului, plus trei locale
 * (`timeout`, `network_error`, `bad_response`) pentru situatiile in care nu
 * exista niciun raspuns de la api pe care sa scrie un cod - browserul nu
 * distinge CORS blocat de DNS gresit de api oprit, deci nici mesajul nu
 * pretinde ca le distinge.
 */

import { t } from '../lib/i18n';

/** Codurile din §6, plus cele produse local cand nu exista raspuns. */
export type ApiErrorCode =
  | 'unauthenticated'
  | 'bad_credentials'
  | 'refresh_expired'
  | 'forbidden'
  | 'no_local_account'
  | 'account_disabled'
  | 'not_found'
  | 'self_approval'
  | 'unknown_step'
  | 'circular_dependency'
  | 'validation_failed'
  | 'conflict'
  | 'server_error'
  | 'timeout'
  | 'network_error'
  | 'bad_response'
  | 'unknown_error'
  | (string & {});

export class ApiError extends Error {
  readonly code: ApiErrorCode;
  readonly status: number;
  /** `data.fields` la `validation_failed`; altfel de obicei null. */
  readonly fields: Record<string, string> | null;

  constructor(
    message: string,
    code: ApiErrorCode,
    status: number,
    fields: Record<string, string> | null = null,
  ) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.fields = fields;
  }
}

/** Ce cod se presupune cand api-ul intoarce un status http fara corp cu `code`. */
export function codeForStatus(status: number): ApiErrorCode {
  if (status === 401) return 'unauthenticated';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not_found';
  if (status === 409) return 'conflict';
  if (status === 422) return 'validation_failed';
  if (status >= 500) return 'server_error';
  return 'unknown_error';
}

/** Mesajul de rezerva, cand api-ul n-a trimis unul. Scris pentru om, nu pentru log. */
export function messageForStatus(status: number): string {
  /* Traduse la CITIRE, nu la scriere: `t` se uita la limba de acum, iar
     mesajul se construieste chiar in clipa in care va fi si aratat. */
  if (status === 403) return t('You do not have permission to do this.');
  if (status === 404) return t('This item does not exist, or it is not available to your company.');
  if (status === 409) return t('This conflicts with the current state of the request.');
  if (status >= 500) return t('The service is currently unavailable. Please try again later.');
  return t('The request could not be completed.');
}

/** Orice `unknown` prins intr-un `catch` devine un ApiError afisabil. */
export function toApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error;
  if (error instanceof Error) return new ApiError(error.message, 'unknown_error', 0);
  return new ApiError(t('Something went wrong.'), 'unknown_error', 0);
}
